import React, { useEffect, useState } from 'react'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Input from 'components/input'
import Tooltip from 'components/tooltip'
import { Copy, Tick } from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import { BLUE_BUTTON } from 'components/save-buttons'

/**
 * The phrase to type, underlined, with a small button after it that copies it (if it may be copied) and
 * ticks for a moment once it has.
 */
const Phrase: React.FC<{ children: string; copyable?: boolean }> = ({ children, copyable }) => {
  const { setSnackbarMessage } = useApplicationStore()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])

  return (
    <span className="inline-flex items-center gap-0.5">
      <span className="font-semibold underline decoration-faint underline-offset-4">{children}</span>
      {copyable && (
        <Tooltip content={copied ? 'Copied' : 'Copy'}>
          <Button
            internalType="transparent"
            aria-label={`Copy ${children}`}
            className="size-6 p-0 [&_svg]:size-3.5"
            onClick={() =>
              // Started inside a promise, so a browser without the clipboard (which throws at once) is caught too.
              Promise.resolve()
                .then(() => navigator.clipboard.writeText(children))
                .then(() => setCopied(true))
                .catch(() =>
                  setSnackbarMessage({ status: 'error', message: "This couldn't be copied. Type it instead." })
                )
            }
          >
            {copied ? <Tick /> : <Copy />}
          </Button>
        </Tooltip>
      )}
    </span>
  )
}

interface ActionDialogProps {
  title: string
  description: string
  confirmLabel: string
  /** Red, for an action that removes or stops something; otherwise the product's blue. */
  danger?: boolean
  /** What to type before the action can be confirmed, if anything. */
  phrase?: string
  /** Whether the phrase can be copied, rather than typed out as a last check. */
  copyable?: boolean
  onClose: () => void
  /** Does it; rejects, having said why, if it fails, and the dialog stays open to try again. */
  onConfirm: () => Promise<void>
}

/**
 * Confirms an action in our dialog: what it does and, for one that's hard to undo, a phrase to type first
 * (a name, which can be copied, or a sentence typed out), then Cancel or the action.
 */
const ActionDialog: React.FC<ActionDialogProps> = ({
  title,
  description,
  confirmLabel,
  danger = false,
  phrase,
  copyable = false,
  onClose,
  onConfirm
}) => {
  const [typed, setTyped] = useState('')
  const [saving, setSaving] = useState(false)
  const confirmed = !phrase || typed.trim() === phrase.trim()

  const confirm = async () => {
    setSaving(true)
    try {
      await onConfirm()
      setSaving(false)
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && !saving && onClose()}
      title={title}
      description={description}
      width="small"
      footer={
        <>
          <Button internalType="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            internalType={danger ? 'danger' : 'outline'}
            className={danger ? undefined : BLUE_BUTTON}
            isLoading={saving}
            disabled={!confirmed}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {phrase && (
        <div className="flex flex-col gap-1.5">
          {/* Not a <label>, which would name the copy button inside it too: the field is named on its own. */}
          <div className="text-[13px] font-medium leading-6 text-body">
            Type <Phrase copyable={copyable}>{phrase}</Phrase> to confirm
          </div>
          <Input
            aria-label={`Type ${phrase} to confirm`}
            autoComplete="off"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
      )}
    </Dialog>
  )
}

export default ActionDialog
